import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import client from '../api/client';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

export default function Tracking() {
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState<{[key: number]: boolean}>({});

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await client.get('/tracking/auto-track-list');
      setProducts(res.data.products);
      const initCollapsed: {[key: number]: boolean} = {};
      res.data.products.forEach((p: any) => { initCollapsed[p.id] = true; });
      setCollapsed(initCollapsed);
    } catch {} finally {
      setLoading(false);
    }
  };

  const handleDeleteKeyword = async (keywordId: number, productId: number) => {
    if (!window.confirm('이 키워드 데이터를 삭제할까요?')) return;
    try {
      await client.delete(`/shopping/keyword/${keywordId}`);
      setProducts(prev => prev.map(p => p.id === productId
        ? { ...p, keywords: p.keywords.filter((k: any) => k.keyword_id !== keywordId) }
        : p
      ));
    } catch { alert('삭제 실패'); }
  };

  const toggleCollapse = (id: number) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const s = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 } as any,
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: '#1E2535', border: '0.5px solid #2A3045', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#8892A4', fontSize: 11 }}>{label}</div>
          <div style={{ color: '#00D4AA', fontSize: 14, fontWeight: 600 }}>
            {payload[0].value ? `${payload[0].value}위` : '미노출'}
          </div>
        </div>
      );
    }
    return null;
  };

  const getStatusInfo = (status: string, rank: number, prevRank: number) => {
    if (!rank) return { label: '이탈', color: '#F56565', icon: '✕' };
    if (status === 'new') return { label: '진입', color: '#378ADD', icon: '★' };
    if (status === 'up') return { label: `▲ ${prevRank - rank}`, color: '#00D4AA', icon: '▲' };
    if (status === 'down') return { label: `▼ ${rank - prevRank}`, color: '#F56565', icon: '▼' };
    return { label: '유지', color: '#8892A4', icon: '—' };
  };

  const getKeywordStats = (history: any[]) => {
    const ranks = history.filter(h => h.rank).map(h => h.rank);
    if (ranks.length === 0) return { best: null, worst: null, avg: null };
    return {
      best: Math.min(...ranks),
      worst: Math.max(...ranks),
      avg: Math.round(ranks.reduce((a, b) => a + b, 0) / ranks.length)
    };
  };

  const getAlerts = (keywords: any[]) => {
    return keywords.filter(kw => {
      if (!kw.rank || !kw.prev_rank) return false;
      return Math.abs(kw.rank - kw.prev_rank) >= 5;
    });
  };

  const getProductSummary = (keywords: any[]) => {
    const ranks = keywords.filter(k => k.rank).map(k => k.rank);
    const bestRank = ranks.length > 0 ? Math.min(...ranks) : null;
    const alertCount = getAlerts(keywords).length;
    return { bestRank, alertCount, totalKeywords: keywords.length };
  };

  return (
    <Layout>
      <div>
        <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>순위 변동 추적</h2>
        <p style={{ color: '#8892A4', fontSize: 12, margin: '0 0 8px' }}>자동순위추적 ON 상품의 키워드별 순위 변동을 확인하세요</p>
        <div style={{ background: '#1A2035', border: '1px solid #378ADD55', borderRadius: 6, padding: '8px 14px', marginBottom: 20, display: 'inline-block' }}>
          <span style={{ color: '#378ADD', fontSize: 12 }}>💡 순위 변동 자동 추적은 <strong>쇼핑 순위 조회</strong> 메뉴에서 추가 · 삭제할 수 있습니다</span>
        </div>

        {loading && <div style={{ color: '#8892A4', fontSize: 14 }}>불러오는 중...</div>}

        {!loading && products.length === 0 && (
          <div style={{ ...s.card, textAlign: 'center', padding: 40 }}>
            <div style={{ color: '#8892A4', fontSize: 14, marginBottom: 8 }}>자동순위추적 중인 상품이 없어요</div>
            <div style={{ color: '#4A5568', fontSize: 12 }}>쇼핑 순위 조회 메뉴에서 "자동순위추적" 버튼을 켜주세요</div>
          </div>
        )}

        {products.map(p => {
          const isCollapsed = collapsed[p.id];
          const alerts = getAlerts(p.keywords);
          const summary = getProductSummary(p.keywords);

          return (
            <div key={p.id} style={s.card}>
              {/* 상품 헤더 */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: isCollapsed ? 0 : 16, paddingBottom: isCollapsed ? 0 : 16, borderBottom: isCollapsed ? 'none' : '0.5px solid #2A3045' }}>
                {p.thumbnail && (
                  <img src={p.thumbnail} alt={p.name} referrerPolicy="no-referrer"
                    style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ color: '#8892A4', fontSize: 12 }}>{p.store_name}</span>
                    <span style={{ color: '#4A5568', fontSize: 12 }}>키워드 {summary.totalKeywords}개</span>
                    {summary.bestRank && <span style={{ color: '#00D4AA', fontSize: 12 }}>최고 {summary.bestRank}위</span>}
                    {summary.alertCount > 0 && (
                      <span style={{ background: '#2A1A1A', color: '#F56565', fontSize: 11, padding: '2px 8px', borderRadius: 4 }}>
                        급변동 {summary.alertCount}개
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => toggleCollapse(p.id)}
                  style={{ background: 'none', border: '0.5px solid #2A3045', borderRadius: 6, padding: '4px 12px', color: '#8892A4', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                  {isCollapsed ? '펼치기 ▼' : '접기 ▲'}
                </button>
              </div>

              {!isCollapsed && (
                <>
                  {/* 급변동 알림 배지 */}
                  {alerts.length > 0 && (
                    <div style={{ background: '#1A1A2A', border: '0.5px solid #2A2A4A', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                      <div style={{ color: '#8892A4', fontSize: 11, marginBottom: 8 }}>⚡ 급변동 알림 (5위 이상 변동)</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {alerts.map((kw: any) => {
                          const diff = kw.prev_rank - kw.rank;
                          return (
                            <div key={kw.keyword_id} style={{ background: diff > 0 ? '#0F2A22' : '#2A1A1A', borderRadius: 6, padding: '4px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{ color: '#E8ECF4', fontSize: 12 }}>{kw.keyword}</span>
                              <span style={{ color: diff > 0 ? '#00D4AA' : '#F56565', fontSize: 12, fontWeight: 700 }}>
                                {diff > 0 ? `▲ ${diff}` : `▼ ${Math.abs(diff)}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* 키워드 목록 */}
                  {p.keywords.length === 0 ? (
                    <div style={{ color: '#4A5568', fontSize: 13 }}>분석하기에서 키워드를 조회해주세요</div>
                  ) : (
                    p.keywords.map((kw: any) => {
                      const statusInfo = getStatusInfo(kw.status, kw.rank, kw.prev_rank);
                      const stats = getKeywordStats(kw.history);
                      const graphData = kw.history.map((h: any) => ({
                        date: h.checked_at.split(' ')[0],
                        rank: h.rank || null
                      }));

                      return (
                        <div key={kw.keyword_id} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '0.5px solid #2A3045' }}>
                          {/* 키워드 + 현재 순위 */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{kw.keyword}</span>
                              <button onClick={() => handleDeleteKeyword(kw.keyword_id, p.id)}
                                style={{ background: 'none', border: '0.5px solid #4A2A2A', borderRadius: 4, padding: '2px 8px', color: '#F56565', fontSize: 10, cursor: 'pointer' }}>삭제</button>
                              <span style={{ background: statusInfo.color + '22', color: statusInfo.color, fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                                {statusInfo.label}
                              </span>
                            </div>
                            {kw.rank && (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <div style={{ background: '#0F2A22', borderRadius: 6, padding: '4px 10px', textAlign: 'center' }}>
                                  <div style={{ fontSize: 10, color: '#4A5568' }}>페이지</div>
                                  <div style={{ fontSize: 14, fontWeight: 600, color: '#00D4AA' }}>{kw.page}p</div>
                                </div>
                                <div style={{ background: '#0D1F35', borderRadius: 6, padding: '4px 10px', textAlign: 'center' }}>
                                  <div style={{ fontSize: 10, color: '#4A5568' }}>순위</div>
                                  <div style={{ fontSize: 14, fontWeight: 600, color: '#378ADD' }}>{kw.rank}위</div>
                                </div>
                              </div>
                            )}
                            {!kw.rank && <span style={{ color: '#F56565', fontSize: 13 }}>20페이지 내 노출 없음</span>}
                          </div>

                          {/* 통계 요약 */}
                          {stats.best && (
                            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                              <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                                <span style={{ color: '#4A5568' }}>최고 </span>
                                <span style={{ color: '#00D4AA', fontWeight: 600 }}>{stats.best}위</span>
                              </div>
                              <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                                <span style={{ color: '#4A5568' }}>최저 </span>
                                <span style={{ color: '#F56565', fontWeight: 600 }}>{stats.worst}위</span>
                              </div>
                              <div style={{ background: '#0F1117', borderRadius: 6, padding: '4px 10px', fontSize: 11 }}>
                                <span style={{ color: '#4A5568' }}>평균 </span>
                                <span style={{ color: '#8892A4', fontWeight: 600 }}>{stats.avg}위</span>
                              </div>
                            </div>
                          )}

                          {/* 그래프 */}
                          {graphData.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                              <ResponsiveContainer width="100%" height={100}>
                                <LineChart data={graphData}>
                                  <XAxis dataKey="date" tick={{ fill: '#4A5568', fontSize: 9 }} />
                                  <YAxis reversed domain={['dataMin - 2', 'dataMax + 2']} tick={{ fill: '#4A5568', fontSize: 9 }} />
                                  <Tooltip content={<CustomTooltip />} />
                                  <Line type="monotone" dataKey="rank" stroke="#00D4AA" strokeWidth={2}
                                    dot={(props: any) => {
                                      const { cx, cy, payload } = props;
                                      if (!payload.rank) return <circle key={props.key} cx={cx} cy={cy} r={4} fill="#F56565" />;
                                      return <circle key={props.key} cx={cx} cy={cy} r={3} fill="#00D4AA" />;
                                    }}
                                    connectNulls={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          )}

                          {/* 기록 테이블 */}
                          {kw.history.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                              <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4 }}>날짜</div>
                              <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4, textAlign: 'right' }}>순위</div>
                              {[...kw.history].reverse().slice(0, 7).map((h: any, j: number) => (
                                <>
                                  <div key={`d${j}`} style={{ color: '#8892A4', fontSize: 12, padding: '2px 0' }}>{h.checked_at.split(' ')[0]}</div>
                                  <div key={`r${j}`} style={{ color: h.rank ? '#00D4AA' : '#F56565', fontSize: 12, padding: '2px 0', textAlign: 'right', fontWeight: 600 }}>
                                    {h.rank ? `${h.rank}위` : '미노출'}
                                  </div>
                                </>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
